const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp']);

export function isImageFile(file) {
  if (!file) return false;
  if (file.type?.startsWith('image/')) return true;
  const ext = file.name?.split('.').pop()?.toLowerCase();
  return ext ? IMAGE_EXTENSIONS.has(ext) : false;
}

function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read image file'));
    };
    img.src = url;
  });
}

/**
 * Resize and re-encode large phone photos before upload to avoid mobile network failures.
 */
export async function compressImageForUpload(file, options = {}) {
  if (!file || !isImageFile(file)) {
    throw new Error('Please select a valid image file (JPG, PNG, GIF, or WebP).');
  }

  const maxWidth = options.maxWidth ?? 1400;
  const maxHeight = options.maxHeight ?? 1400;
  const maxBytes = options.maxBytes ?? 2 * 1024 * 1024;
  const skipBelowBytes = options.skipBelowBytes ?? 600 * 1024;

  if (file.type === 'image/gif' || file.size <= skipBelowBytes) {
    return file;
  }

  let image;
  try {
    image = await loadImageFromFile(file);
  } catch {
    return file;
  }

  let width = image.naturalWidth || image.width;
  let height = image.naturalHeight || image.height;
  if (!width || !height) {
    return file;
  }

  const scale = Math.min(1, maxWidth / width, maxHeight / height);
  width = Math.max(1, Math.round(width * scale));
  height = Math.max(1, Math.round(height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return file;
  }
  ctx.drawImage(image, 0, 0, width, height);

  const qualities = [0.88, 0.8, 0.72, 0.64];
  let bestBlob = null;

  for (const quality of qualities) {
    // eslint-disable-next-line no-await-in-loop
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
    if (!blob) continue;
    bestBlob = blob;
    if (blob.size <= maxBytes) break;
  }

  if (!bestBlob || bestBlob.size >= file.size) {
    return file;
  }

  const baseName = (file.name || 'photo').replace(/\.[^.]+$/, '') || 'photo';
  return new File([bestBlob], `${baseName}.jpg`, {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });
}
